/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { AccordionGroup, Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';
import type { AccordionGroupProps } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import { twoPanelGridItemFullWidth } from 'bundles/unified-description-page-common/constants/styles';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

interface Props {
  title: string | React.ReactNode;
  description?: React.ReactNode;
  syllabusItems: AccordionGroupProps['children'];
  sidebar?: React.ReactNode;
  anchorId?: string;
  footer?: React.ReactNode;
  addExtraDesktopPadding?: boolean;
  slug?: string;
}

const styles = {
  description: css`
    margin-top: var(--cds-spacing-300);

    ${breakpoints.up('sm')} {
      padding-bottom: var(--cds-spacing-100);
    }
  `,
  syllabusContent: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    overflow: clip;
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  accordionGroup: (addExtraPadding?: boolean) => css`
    padding: ${addExtraPadding ? 'calc(var(--cds-spacing-200) + var(--cds-spacing-100) / 4)' : 'var(--cds-spacing-100)'}
      var(--cds-spacing-400) var(--cds-spacing-100);
  `,
  sidebar: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-100) var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
      padding: 0 var(--cds-spacing-200);
    }
  `,
  footer: css`
    &:has(> *) {
      display: flex;
      flex-direction: column;
      gap: var(--cds-spacing-300);
      border-top: 2px solid var(--cds-color-grey-50);
      margin: 0 var(--cds-spacing-400) var(--cds-spacing-100) var(--cds-spacing-400);
      padding: var(--cds-spacing-300) 0;

      ${breakpoints.down('xs')} {
        margin: 0;
        padding: var(--cds-spacing-200);
      }
    }
  `,
  badgeSpacing: css`
    /* Handle LTR and RTL */
    display: inline-block;
    padding-left: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-100);
  `,
};

const SyllabusModule = ({
  title,
  description,
  syllabusItems,
  sidebar,
  anchorId,
  footer,
  addExtraDesktopPadding,
  slug,
}: Props) => {
  const isMobile = useIsMobile();
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'syllabus',
      },
      ...eventingProductData,
    }
  );

  const items = useMemo(() => {
    if (!syllabusItems || !(Array.isArray(syllabusItems) && syllabusItems.length)) {
      return null;
    }
    return syllabusItems.map((item) => item);
  }, [syllabusItems]);

  if (!items) {
    return null;
  }

  return (
    <Section anchorId={anchorId} ref={visibilityTrackingRef}>
      <PageGridContainer>
        <Grid item xs={12} lg={8} md={7} css={twoPanelGridItemFullWidth}>
          <Typography variant="h1semibold" component="h2" css={{ display: 'flex' }}>
            {title}
            <div css={styles.badgeSpacing}>
              <NewAiBadge slug={slug} />
            </div>
          </Typography>
          {description && <div css={styles.description}>{description}</div>}
        </Grid>
        <Grid item xs={12} lg={8} md={7} css={twoPanelGridItemFullWidth}>
          <TrackedDiv
            trackingName="syllabus"
            trackClicks={false}
            requireFullyVisible={false}
            withVisibilityTracking={true}
          >
            <div css={styles.syllabusContent}>
              {isMobile ? (
                <React.Fragment>{items}</React.Fragment>
              ) : (
                <AccordionGroup css={styles.accordionGroup(addExtraDesktopPadding)} multiExpand spacing={8}>
                  {items}
                </AccordionGroup>
              )}
              {footer && <div css={styles.footer}>{footer}</div>}
            </div>
          </TrackedDiv>
        </Grid>
        <Grid item xs={12} lg={4} md={5} css={twoPanelGridItemFullWidth}>
          {sidebar && <div css={styles.sidebar}>{sidebar}</div>}
        </Grid>
      </PageGridContainer>
    </Section>
  );
};

export default SyllabusModule;
